import { useState, useEffect } from 'react';

/**
 * Reads the device screen orientation type string from the Screen Orientation API, returning `'portrait-primary'` or `'landscape-primary'` on supported browsers.
 *
 * Returns `undefined` if `window.screen` or `screen.orientation` is not available — e.g. in SSR, non-browser environments, or browsers that do not support the API.
 *
 * @returns A `ScreenOrientationType` string (e.g. `'portrait-primary'`) or `undefined`.
 */
const getOrientation = () => window.screen?.orientation?.type;

/**
 * Tracks the device's current screen orientation, updating on window resize (a proxy for orientation change since not all browsers fire a dedicated `orientationchange` event reliably).
 *
 * @returns The current `ScreenOrientation` type string (e.g. `'portrait-primary'`), or `undefined` if unsupported.
 */
export const useDeviceOrientation = () => {
  const [orientation, setOrientation] = useState(getOrientation());

  const updateOrientation = () => {
    setOrientation(getOrientation());
  };

  useEffect(() => {
    window.addEventListener('resize', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);

  return orientation;
};

export default useDeviceOrientation;
